import { Environment } from '@/constants';
import { getChEventsExperimentUrl } from '@/libs/analytics/ch-events-config';
import { extractAppConfig } from '@/utils/app-config';
import { getUserBrowser, getUserOs } from '@/utils/browser';
import { isAdult } from '@/utils/date-time';
import {
    Experiment,
    ExperimentClient,
    ExperimentConfig,
    ExperimentUser,
    Variants,
} from '@amplitude/experiment-js-client';
import { getCookie } from '@calm/web-common/dist/cookies';
import isSSR from '@calm/web-common/dist/isSSR';
import { ExperimentUserProps } from '@rhg/providers/experiment-client/types';

const {
    NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_DEPLOYMENT_KEY: experimentsDeploymentKey,
    NEXT_PUBLIC_CYPRESS_AMPLITUDE_CLIENT_ENABLED: cypressAmplitudeClientEnabled,
    NEXT_PUBLIC_ENV: currentEnvironment,
} = extractAppConfig();

export const E2E_AMPLITUDE_FLAGS_COOKIE = 'e2eAmplitudeFlags';

const environmentsSupportingE2eAmplitudeOverrides: Environment[] = [
    Environment.LOCAL,
    Environment.DEV,
    Environment.STAGING,
];

type AmplitudeFlagOverrideValue = string | number | boolean;

const buildInitialVariants = (
    overrides: Record<string, AmplitudeFlagOverrideValue>
): Variants => {
    const variants: Variants = {};
    for (const [key, value] of Object.entries(overrides)) {
        if (typeof value === 'boolean') {
            variants[key] = { value: value ? 'on' : 'off' };
        } else {
            variants[key] = { value: String(value) };
        }
    }
    return variants;
};

const getE2eAmplitudeFlagOverrides = ():
    | Record<string, AmplitudeFlagOverrideValue>
    | undefined => {
    if (
        isSSR() ||
        !environmentsSupportingE2eAmplitudeOverrides.includes(
            currentEnvironment as Environment
        )
    ) {
        return undefined;
    }

    const cookieValue = getCookie(E2E_AMPLITUDE_FLAGS_COOKIE);
    if (!cookieValue) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(decodeURIComponent(cookieValue));
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as Record<string, AmplitudeFlagOverrideValue>;
        }
    } catch (error) {
        console.error(
            'Failed to parse e2e Amplitude feature flag overrides from cookie',
            error
        );
    }
    return undefined;
};

export const AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE = 'client-unavailable';
export const AMPLITUDE_WEB_PLATFORM_IDENTIFIER = 'Web';

export type AmplitudeExperimentClient =
    | ExperimentClient
    | typeof AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE;

// Cypress attaches its own global; the SDK client is hung off it for E2E access.
type CypressWindow = Window & {
    Cypress?: { amplitudeExperimentClient?: AmplitudeExperimentClient };
};

interface ExperimentClientConfig {
    experimentProxyEnabled?: boolean;
    chEventsProxyUrl?: string;
}

export const initAmplitudeExperimentClient = async (
    config?: ExperimentClientConfig
): Promise<AmplitudeExperimentClient> => {
    if (!experimentsDeploymentKey) {
        throw Error('Missing amplitude experiments deployment key');
    }

    try {
        const experimentConfig: ExperimentConfig = {
            instanceName: 'frontend',
        };

        if (config?.experimentProxyEnabled && config?.chEventsProxyUrl) {
            experimentConfig.serverUrl = getChEventsExperimentUrl(
                config.chEventsProxyUrl
            );
        }

        // E2E override: when the e2eAmplitudeFlags cookie is present in a
        // non-prod environment, wrap .variant() so cookie values short-circuit
        // the SDK lookup. The wrap covers both remote- and locally-evaluated
        // flags — locally-evaluated flags run segment-targeting against flag
        // configs fetched on start() and ignore the SDK's own initialVariants
        // fallback, so config-level seeding isn't sufficient. fetchOnStart
        // skips the call to api.lab.amplitude.com/sdk/v2/vardata since
        // overridden flags never read it (the flag-config fetch for local
        // eval still happens — pollOnStart only gates the periodic re-poll).
        const e2eOverrides = getE2eAmplitudeFlagOverrides();
        let overrideVariants: Variants | undefined;
        if (e2eOverrides) {
            overrideVariants = buildInitialVariants(e2eOverrides);
            experimentConfig.fetchOnStart = false;
        }

        const experimentClient = Experiment.initializeWithAmplitudeAnalytics(
            experimentsDeploymentKey,
            experimentConfig
        );
        await experimentClient.start();

        if (overrideVariants) {
            const overrides = overrideVariants;
            const origVariant = experimentClient.variant.bind(experimentClient);
            experimentClient.variant = (
                key: string,
                fallback?: Parameters<typeof origVariant>[1]
            ) =>
                // Object.hasOwn (not `in`) so inherited properties on the
                // override object's prototype (e.g. `toString`) don't
                // accidentally short-circuit the SDK lookup.
                Object.hasOwn(overrides, key)
                    ? overrides[key]
                    : origVariant(key, fallback);

            const origAll = experimentClient.all.bind(experimentClient);
            experimentClient.all = () => ({ ...origAll(), ...overrides });

            const origExposure =
                experimentClient.exposure.bind(experimentClient);
            experimentClient.exposure = (key: string) => {
                if (!Object.hasOwn(overrides, key)) {
                    origExposure(key);
                }
            };
        }

        /*
         * Need to add this to both the parent and child window for Cypress to access it
         * See: https://github.com/cypress-io/cypress/issues/25394
         */
        const cypressWindow = window as CypressWindow;
        if (cypressWindow.Cypress && cypressAmplitudeClientEnabled) {
            cypressWindow.Cypress.amplitudeExperimentClient = experimentClient;
        }
        const cypressParent = window.parent as CypressWindow;
        if (
            cypressParent &&
            cypressParent.Cypress &&
            cypressAmplitudeClientEnabled
        ) {
            cypressParent.Cypress.amplitudeExperimentClient = experimentClient;
        }

        return experimentClient;
    } catch {
        return AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE;
    }
};

export const setupAmplitudeExperimentUser = ({
    partnerId,
    country,
    language,
    isDependent,
    isConciergeEnabled,
    isJournalingEnabled,
    dob,
}: ExperimentUserProps): ExperimentUser => {
    const os = getUserOs();
    const browser = getUserBrowser().name;

    // Left undefined when the dob is unknown so it is omitted from the user
    // properties rather than reported as a misleading `false`.
    const userIsAdult = isAdult(dob);

    return {
        os: `${os.name} ${os.version}`,
        device_model: browser,
        platform: AMPLITUDE_WEB_PLATFORM_IDENTIFIER,
        language,
        user_properties: {
            // todo: confirm no usage of this casing before removal
            partnerId: partnerId ?? '',
            // adding for casing consistency w/in Amplitude
            PartnerID: partnerId ?? '',
            ...(country ? { countryCode: country } : {}),
            ...(isDependent !== undefined ? { isDependent } : {}),
            ...(isConciergeEnabled !== undefined ? { isConciergeEnabled } : {}),
            ...(isJournalingEnabled !== undefined
                ? { isJournalingEnabled }
                : {}),
            ...(userIsAdult !== undefined ? { isAdult: userIsAdult } : {}),
        },
    };
};

/**
 * Only used for experiments: `remote`-evaluated flags with `control` and `treatment`
 * variants, analyzed against `[Experiment] Exposure` events. NOT for feature flags.
 * See the README "Experiments" section.
 *
 * ex: EXAMPLE_EXPERIMENT = 'example_experiment_demo_only',
 */
export enum AmplitudeExperimentNames {
    DISCOVER_POST_ONBOARDING = 'discover-post-onboarding',
    DISABLE_ONBOARDING_VIDEOS = 'disable-onboarding-videos-v2',
}

/**
 * Only used for feature flags, that gate features under development or staggered deployment.
 * Ensure the value matches the Amplitude flag key exactly.
 *
 * ex: EXAMPLE_FLAG_NAME = 'example_experiment_demo_only',
 */
export enum AmplitudeFeatureFlagNames {
    FTUE_WORKFLOW_ENABLED = 'ftue-workflow-enabled',
    THEME_SELECTOR_ENABLED = 'theme-selector-enabled',
    MINDSHIFT_PROMO_DIALOG_SNOOZE_DURATION_SECONDS = 'mindshift-promo-dialog-snooze-duration-seconds',
    CONCIERGE_GA_ENABLED = 'concierge-early-access',
    P4P_PARTNER_REWARDS_ENABLED = 'p4p-partner-rewards-enabled',
    DYNAMIC_HOME_ENABLED = 'dynamic-home-enabled',
    FEATURED_HERO_CAROUSEL_CONTENT = 'featured-hero-carousel-content-web',
    BACKEND_FOR_FRONTEND_ENABLED = 'backend-for-frontend-enabled',
    DISCOVER_COLLECTIONS_ENABLED = 'calm-health-discover-collections-enabled',
    DATAGRAIL_DELETION_ENABLED = 'calm-health-datagrail-deletion-enabled',
    LANDING_PAGE_REDESIGN_ENABLED = 'landing-page-redesign-enabled',
    BUY_UP_ENABLED = 'buy-up-enabled',
    EMOTIONS_CHECK_IN_ENABLED = 'emotions-check-in-enabled',
    PLAYLISTS_ENABLED_WEB = 'playlists-enabled-web',
    SHORTCUTS_ENABLED = 'shortcuts-enabled',
}
